import React, { createContext, useState, useContext, useEffect } from "react";
import { localAuthService } from "../services/auth/localAuthService";
import { safeReadJsonFromLocalStorage } from "../utils/storage";

// Behörigheter (oförändrade)
const pagePermissions = {
  statementOfFact: ["admin", "master", "chiefofficer", "officer"],
  nor: ["admin", "master", "chiefofficer"],
  ctc: ["admin", "master", "chiefofficer"],
  slop: ["admin", "master", "chiefofficer"],
  rcs: ["admin", "master", "chiefofficer"],
  logging: ["admin", "master", "chiefofficer", "officer"],
  pumpstatement: ["admin", "master", "chiefofficer"],
  protests: ["admin", "master", "chiefofficer"],
  obqrob: ["admin", "master", "chiefofficer"],
  checklists: ["admin", "master", "chiefofficer", "officer", "crew"],
  etc: ["admin", "master", "chiefofficer"],
  deadweight: ["admin", "master", "chiefofficer"],
  settings: ["admin", "master"],
  bargelogs: ["admin", "master", "chiefofficer", "officer"],
  shipParticulars: ["admin", "master", "chiefofficer", "officer", "crew"],
};

const UserContext = createContext();
export const useUserContext = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const storedUsers = safeReadJsonFromLocalStorage("users", []);
    if (Array.isArray(storedUsers)) {
      setUsers(storedUsers);
    }

    const vesselId =
      localStorage.getItem("provisionedVesselId") ||
      safeReadJsonFromLocalStorage("activeShipData", null)?.id;

    if (!vesselId) return;

    localAuthService
      .resolveCurrentSession(vesselId)
      .then((result) => {
        if (result?.user) {
          setUser(result.user);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (!user?.localSessionId) return undefined;

    const touchSession = () => {
      localAuthService.touchSession(user.localSessionId).catch((error) => {
        console.error(error);
      });
    };

    const intervalId = window.setInterval(touchSession, 60 * 1000);
    window.addEventListener("pointerdown", touchSession);
    window.addEventListener("keydown", touchSession);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pointerdown", touchSession);
      window.removeEventListener("keydown", touchSession);
    };
  }, [user?.localSessionId]);

  const setUserData = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem("currentUser", JSON.stringify(userData));
    } else {
      localStorage.removeItem("currentUser");
    }
  };

  const setUsersData = (usersData) => {
    setUsers(usersData);
    localStorage.setItem("users", JSON.stringify(usersData));
  };

  const logout = async () => {
    const vesselId = user?.vesselId || user?.shipId;
    await localAuthService.logout(vesselId);
    setUserData(null);
  };

  const hasAccessToPage = (page) => {
    if (!user) return false;
    const userRoles = user?.roles || [];
    const allowedRoles = pagePermissions[page] || [];
    if (allowedRoles.length === 0) return true;
    return allowedRoles.some((role) => userRoles.includes(role));
  };

  return (
    <UserContext.Provider
      value={{
        user,
        users,
        setUsersData,
        setUserData,
        logout,
        hasAccessToPage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
